import { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { Server, Monitor, Link2, NetworkIcon } from 'lucide-react';
import { InfraDashboard } from '../components/dashboard/infra/InfraDashboard';
import { MicroDashboard } from '../components/dashboard/micro/MicroDashboard';
import { NetworkDashboard } from '../components/dashboard/network/NetworkDashboard';
import { IpamDashboard } from '../components/dashboard/ipam/IpamDashboard';

// Claves canónicas de las API que alimentan cada indicador.
const PERMISSIONS = {
  dashboard: 'core.dashboard', inventory: 'reports.inventory',
  servers: 'assets.servers', switches: 'assets.switches', firewalls: 'assets.firewalls',
  ups: 'assets.ups', storage: 'assets.storage', access_points: 'assets.access_points',
  sql_servers: 'assets.sql', cameras: 'assets.camaras', dns_critical: 'reports.dns_traceability',
  workstations: 'assets.workstations', printers: 'micro.printers', tablets: 'micro.tablets',
  cellphones: 'micro.mobile_devices', monitors: 'micro.monitores', tvs: 'micro.tvs',
  ip_phones: 'micro.telefonos_ip', projectors: 'micro.proyectores', clocks: 'micro.relojes',
  enlaces: 'assets.enlaces', certificates: 'assets.certificates', licenses: 'assets.licencias', dns: 'assets.dns',
  subnets: 'ipam.subnets', ips: 'ipam.addresses', vlans: 'ipam.vlans', l2_domains: 'ipam.l2_domains',
};

const TAB_MODULES = {
  infra: ['servers', 'switches', 'firewalls', 'ups', 'storage', 'access_points', 'sql_servers', 'cameras', 'dns_critical'],
  micro: ['workstations', 'printers', 'tablets', 'cellphones', 'monitors', 'tvs', 'ip_phones', 'projectors', 'clocks'],
  network: ['enlaces', 'certificates', 'licenses', 'dns'],
  ipam: ['subnets', 'ips', 'vlans', 'l2_domains'],
};

const specsForTab = (tab, allowed) => {
  const result = [{ key: 'stats', label: 'indicadores', url: `/dashboard/${tab}/stats` }];
  if (tab === 'infra') {
    if (['servers', 'switches', 'firewalls', 'ups', 'storage', 'access_points', 'cameras'].some(key => allowed[key])) {
      result.push({ key: 'site', label: 'infraestructura por site', url: '/dashboard/infra/by-site' });
    }
    if (allowed.servers) result.push({ key: 'types', label: 'tipos de servidor', url: '/dashboard/infra/servers-by-type' });
  } else if (tab === 'micro') {
    result.push({ key: 'site', label: 'dispositivos por site', url: '/dashboard/micro/by-site' });
  } else if (tab === 'network') {
    if (allowed.enlaces) result.push({ key: 'providers', label: 'enlaces por proveedor', url: '/dashboard/network/enlaces-by-provider' });
  } else if (tab === 'ipam') {
    if (allowed.subnets && allowed.ips) result.push({ key: 'utilization', label: 'registro por subnet', url: '/dashboard/ipam/subnet-utilization' });
    if (allowed.ips) result.push({ key: 'site', label: 'direcciones por site', url: '/dashboard/ipam/ips-by-site' });
  }
  return result;
};

const Dashboard = () => {
  const { can } = useAuth();
  const [activeTab, setActiveTab] = useState('');
  const [retry, setRetry] = useState(0);
  const [view, setView] = useState({ tab: '', loading: true, data: {}, errors: [] });

  const allowed = useMemo(() => Object.fromEntries(
    Object.entries(PERMISSIONS).map(([key, page]) => [key, can(page, 'read')])
  ), [can]);
  const permissionKey = JSON.stringify(allowed);
  const visibleTabs = useMemo(() => Object.keys(TAB_MODULES).filter(
    tab => TAB_MODULES[tab].some(key => allowed[key])
  ), [allowed]);

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) setActiveTab(visibleTabs[0] || '');
  }, [visibleTabs, activeTab]);

  useEffect(() => {
    if (!allowed.dashboard || !visibleTabs.includes(activeTab)) return undefined;
    const controller = new AbortController();
    const specs = specsForTab(activeTab, allowed);
    setView({ tab: activeTab, permissionKey, loading: true, data: {}, errors: [] });
    Promise.allSettled(specs.map(spec => api.get(spec.url, { signal: controller.signal, timeout: 15000 })))
      .then(results => {
        if (controller.signal.aborted) return;
        const data = {};
        const errors = [];
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') data[specs[index].key] = result.value.data;
          else errors.push(specs[index].label);
        });
        setView({ tab: activeTab, permissionKey, loading: false, data, errors });
      });
    return () => controller.abort();
  }, [activeTab, allowed, visibleTabs, permissionKey, retry]);

  const noDashboard = !allowed.dashboard;
  if (noDashboard || visibleTabs.length === 0) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="page-header"><h1 className="page-title">Dashboard</h1><p className="page-subtitle">Vista consolidada del inventario IT</p></div>
          <Card className="card-subtle p-8 text-center">
            <p className="text-muted-foreground">{noDashboard ? 'No tiene permiso para ver el Dashboard.' : 'No tiene permisos para ver ningún módulo del Dashboard.'}</p>
          </Card>
        </div>
      </Layout>
    );
  }

  const loading = view.tab !== activeTab || view.permissionKey !== permissionKey || view.loading;
  const data = loading ? {} : view.data;
  const gridCols = Math.min(visibleTabs.length, 4);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Vista del inventario al consultar; no muestra disponibilidad en tiempo real.</p>
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full lg:w-auto lg:inline-grid" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
            {visibleTabs.includes('infra') && <TabsTrigger value="infra" className="flex items-center gap-2" data-testid="tab-infra"><Server className="h-4 w-4" strokeWidth={1.5} /><span className="hidden sm:inline">Infraestructura</span><span className="sm:hidden">Infra</span></TabsTrigger>}
            {visibleTabs.includes('micro') && <TabsTrigger value="micro" className="flex items-center gap-2" data-testid="tab-micro"><Monitor className="h-4 w-4" strokeWidth={1.5} /><span className="hidden sm:inline">Microinformática</span><span className="sm:hidden">Micro</span></TabsTrigger>}
            {visibleTabs.includes('network') && <TabsTrigger value="network" className="flex items-center gap-2" data-testid="tab-network"><Link2 className="h-4 w-4" strokeWidth={1.5} /><span className="hidden sm:inline">Conectividad</span><span className="sm:hidden">Red</span></TabsTrigger>}
            {visibleTabs.includes('ipam') && <TabsTrigger value="ipam" className="flex items-center gap-2" data-testid="tab-ipam"><NetworkIcon className="h-4 w-4" strokeWidth={1.5} /><span>IPAM</span></TabsTrigger>}
          </TabsList>

          {!loading && view.errors.length > 0 && (
            <div role="alert" className="mt-5 flex flex-wrap items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
              No se pudieron cargar: {view.errors.join(', ')}.
              <button type="button" className="text-primary underline" onClick={() => setRetry(value => value + 1)}>Reintentar</button>
            </div>
          )}
          <TabsContent value="infra" className="mt-6" data-testid="panel-infra"><InfraDashboard loading={loading} stats={data.stats ?? null} bySite={data.site ?? null} serversByType={data.types ?? null} allowed={allowed} /></TabsContent>
          <TabsContent value="micro" className="mt-6" data-testid="panel-micro"><MicroDashboard loading={loading} stats={data.stats ?? null} bySite={data.site ?? null} allowed={allowed} /></TabsContent>
          <TabsContent value="network" className="mt-6" data-testid="panel-network"><NetworkDashboard loading={loading} stats={data.stats ?? null} providerData={data.providers ?? null} allowed={allowed} /></TabsContent>
          <TabsContent value="ipam" className="mt-6" data-testid="panel-ipam"><IpamDashboard loading={loading} stats={data.stats ?? null} subnetUtil={data.utilization ?? null} ipsBySite={data.site ?? null} allowed={allowed} /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Dashboard;
